<template>
  <div class="overflow-x-auto shadow-sm rounded-lg border border-gray-200">
    <table class="min-w-full divide-y divide-gray-200 text-[10px] md:text-xs">
      <thead class="bg-gray-50">
        <tr>
          <th
            v-for="(header, index) in headers"
            :key="index"
            :class="[
              'pr-2 md:pr-3 pl-1 py-1 md:py-1.5 text-xs md:text-sm font-bold text-black uppercase tracking-wider text-center',
              sortable && header.sortable ? 'cursor-pointer' : 'cursor-default',
            ]"
            @click="header.key !== 'checkbox' ? handleSort(header.key) : null"
          >
            <div
              :class="[
                'flex items-center',
                (header.headerAlign || header.align) === 'center'
                  ? 'justify-center'
                  : (header.headerAlign || header.align) === 'right'
                  ? 'justify-end'
                  : (header.headerAlign || header.align) === 'left'
                  ? 'justify-start'
                  : 'justify-center',
              ]"
            >
              <!-- Slot pour le header personnalisé -->
              <slot
                :name="`header-${header.key}`"
                :header="header"
                :index="index"
              >
                <span>{{ header.label }}</span>
              </slot>
              <div
                v-if="sortable && header.sortable && header.key !== 'checkbox'"
                class="flex flex-col"
              >
                <i
                  :class="[
                    'fas text-[10px] transition-colors',
                    sortKey === header.key && sortOrder === 'asc'
                      ? 'fa-sort-up text-blue-600'
                      : 'fa-sort-up text-black',
                  ]"
                ></i>
                <i
                  :class="[
                    'fas text-[10px] transition-colors -mt-1',
                    sortKey === header.key && sortOrder === 'desc'
                      ? 'fa-sort-down text-blue-600'
                      : 'fa-sort-down text-black',
                  ]"
                ></i>
              </div>
            </div>
          </th>
        </tr>
      </thead>
      <tbody class="bg-white divide-y divide-gray-200">
        <tr
          v-for="(row, rowIndex) in sortedData"
          :key="row.id || rowIndex"
          :class="[
            'hover:bg-gray-50 transition-colors duration-150 ease-in-out cursor-pointer border-b border-gray-200',
            rowClass ? rowClass(row) : '',
          ]"
          @click="handleRowClick(row)"
        >
          <td
            v-for="(header, colIndex) in headers"
            :key="colIndex"
            :class="[
              header.cellClass || '',
              'px-1 whitespace-nowrap text-black',
              header.cellClass?.includes('py-') ? '' : 'py-1 md:py-1.5',
            ]"
          >
            <div
              :class="[
                'flex items-center',
                header.align === 'center'
                  ? 'justify-center'
                  : header.align === 'right'
                  ? 'justify-end'
                  : header.align === 'left'
                  ? 'justify-start'
                  : 'justify-center',
              ]"
            >
              <!-- Utiliser des slots pour chaque colonne -->
              <slot
                :name="header.key"
                :row="row"
                :header="header"
                :rowIndex="rowIndex"
                :colIndex="colIndex"
              >
                <!-- Fallback : afficher la valeur brute -->
                {{ row[header.key] }}
              </slot>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import type { TableHeader, TableRow } from "@/types";

// Props spécifiques au composant ReusableTable
interface Props {
  headers: TableHeader[];
  data: TableRow[];
  sortable?: boolean;
  rowClass?: (row: TableRow) => string;
}

const props = withDefaults(defineProps<Props>(), {
  sortable: false,
});

// Variables réactives pour le tri
const sortKey = ref<string>("");
const sortOrder = ref<"asc" | "desc">("asc");

// Fonction de tri
const handleSort = (key: string) => {
  // Vérifier si le tri global est activé ET si cette colonne spécifique est triable
  const header = props.headers.find((h) => h.key === key);
  if (!props.sortable || !header?.sortable) return;

  // Si on clique sur la même colonne, on inverse l'ordre
  if (sortKey.value === key) {
    sortOrder.value = sortOrder.value === "asc" ? "desc" : "asc";
  } else {
    // Nouvelle colonne, on commence par ordre croissant
    sortKey.value = key;
    sortOrder.value = "asc";
  }
};

// Données triées
const sortedData = computed(() => {
  if (!sortKey.value || !props.sortable) {
    return props.data;
  }

  // Vérifier si la colonne de tri est triable
  const header = props.headers.find((h) => h.key === sortKey.value);
  if (!header?.sortable) {
    return props.data;
  }

  return [...props.data].sort((a, b) => {
    const aValue = a[sortKey.value];
    const bValue = b[sortKey.value];

    // Gestion des valeurs nulles/undefined
    if (aValue === null || aValue === undefined) return 1;
    if (bValue === null || bValue === undefined) return -1;

    let comparison = 0;

    // Tri numérique
    if (typeof aValue === "number" && typeof bValue === "number") {
      comparison = aValue - bValue;
    }
    // Tri alphabétique (insensible à la casse)
    else if (typeof aValue === "string" && typeof bValue === "string") {
      comparison = aValue.toLowerCase().localeCompare(bValue.toLowerCase());
    }
    // Tri par défaut
    else {
      comparison = String(aValue).localeCompare(String(bValue));
    }

    // Appliquer l'ordre de tri
    return sortOrder.value === "asc" ? comparison : -comparison;
  });
});

// Événements
const emit = defineEmits<{
  "row-click": [row: TableRow];
}>();

const handleRowClick = (row: TableRow) => {
  emit("row-click", row);
};
</script>

<style scoped></style>
